<template>
    <DsSheet
        v-model:open="isOpen"
        primaryLabel="Save"
        secondaryLabel="Cancel"
        title="Edit client"
        @clickPrimary="handleSave"
        @clickSecondary="isOpen = false"
    >
        <ClientFormFields
            v-if="clientEdit"
            v-model:client="clientEdit"
            :clientErrors="emptyErrors"
        />
    </DsSheet>
</template>

<script setup lang="ts">
import { notifyError } from '@/bugsnag';
import type { AddFileClient } from '@/company-portal/add-file/add-file.types';
import DsSheet from '@/components/design-system/DsSheet.vue';
import useDsToast from '@/components/design-system/toast/useDsToast';
import type { ClientFormData, TypedClientError } from '@/components/Views/Company/File/Client/client-info-form.types';
import { ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import ClientFormFields from '@/components/Views/Company/File/Client/ClientFormFields.vue';
import type { ClientType } from '@/components/Views/Company/File/file.types';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { ref, watch } from 'vue';

interface EditAvailableClientSheetProps {
    clientId?: AddFileClient['id'];
}

const props = defineProps<EditAvailableClientSheetProps>();

const isOpen = defineModel<boolean>('open', { required: true });

const fileClientStore = useFileClientStore();

const { addToast } = useDsToast();

const clientEdit = ref<ClientFormData>();

const buildClientEdit = (client: AddFileClient): ClientFormData => ({
    id: client.id,
    first: client.first ?? '',
    last: client.last ?? '',
    email: client.email ?? '',
    phone: client.phone ?? '',
    role: client.role as ClientType | undefined,
});

watch(
    isOpen,
    isNowOpen => {
        const client = isNowOpen ? fileClientStore.findAvailableClientOption(props.clientId) : undefined;
        clientEdit.value = client ? buildClientEdit(client) : undefined;
    },
    { immediate: true },
);

const emptyErrors: TypedClientError = {
    [ClientValidationErrorKeys.EMAIL]: '',
    [ClientValidationErrorKeys.PHONE]: '',
    [ClientValidationErrorKeys.ROLE]: '',
    [ClientValidationErrorKeys.FIRST_NAME]: '',
    [ClientValidationErrorKeys.LAST_NAME]: '',
};

const handleSave = () => {
    if (!clientEdit.value) return;

    const { id, first, last, email, phone, role } = clientEdit.value;
    try {
        fileClientStore.updateAvailableClient(id, {
            first: first ?? '',
            last: last ?? '',
            email,
            phone,
            role: role as AddFileClient['role'] | undefined, // assertion is needed because we have duplicate type declarations for client role -- @todo cleanup in [CLENG-883]
        });
    } catch (error) {
        notifyError(error, 'EditAvailableClientSheet::save', {
            update: clientEdit.value,
            state: {
                userAdded: Array.from(fileClientStore.userAddedClients.values()),
                tps: Array.from(fileClientStore.tpsClients.values()),
            },
        });
    }
    addToast({ detail: 'Client updated' });

    isOpen.value = false;
};
</script>
